import { useCallback, useEffect, useRef } from 'react';
import { getVisitorId } from '../utils/abTest';
import { getDevice, isInternalVisit } from './useCommunityPageAnalytics';
import { isInAppBrowser, inAppName } from '../utils/inAppBrowser';

// Step tracking for the onboarding steps after sign-up (pages/Onboarding.js):
// which steps each visit reached, what stopped them (the "are you sure?"
// pop-ups, location search errors) and whether they went on to Messenger —
// stored by POST /api/analytics/onboarding-view. Components deeper in the page
// (e.g. the location search) report with trackOnboarding(). Best-effort —
// never blocks or throws on the page.

const HEARTBEAT_MS = 15 * 1000;
const MAX_STEPS = 60;
const EVENT = 'ic:onboarding-track';

const newId = () => ((crypto.randomUUID && crypto.randomUUID()) || `ob_${Date.now()}_${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '');

// For components inside the onboarding page; does nothing elsewhere.
export const trackOnboarding = (step, detail) => {
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { step, detail } })); } catch { /* ignore */ }
};

const useOnboardingAnalytics = ({ userId, communityId, totalSteps }) => {
  const ref = useRef(null);
  const info = useRef({ userId, communityId, totalSteps });
  info.current = { userId, communityId, totalSteps };

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    const s = {
      pageViewId: newId(),
      steps: [],
      maxStep: 0,
      maxStepName: '',
      accumulatedMs: 0,
      visibleSince: document.visibilityState === 'visible' ? Date.now() : null,
      exited: false,
    };
    const activeMs = () => s.accumulatedMs + (s.visibleSince ? Date.now() - s.visibleSince : 0);
    const send = (extra = {}) => {
      try {
        fetch(`${apiBase}/analytics/onboarding-view`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify({
            pageViewId: s.pageViewId,
            visitorId: getVisitorId(),
            userId: info.current.userId || undefined,
            communityId: info.current.communityId || undefined,
            totalSteps: info.current.totalSteps || undefined,
            device: getDevice(),
            inApp: isInAppBrowser() ? inAppName() : '',
            internal: isInternalVisit(),
            activeMs: activeMs(),
            maxStep: s.maxStep,
            maxStepName: s.maxStepName || undefined,
            steps: s.steps,
            ...extra,
          }),
        }).catch(() => {});
      } catch { /* ignore */ }
    };
    s.track = (step, detail) => {
      if (s.steps.length >= MAX_STEPS) return;
      if (step === 'step_shown' && detail) {
        const n = parseInt(String(detail), 10);
        if (n > s.maxStep) { s.maxStep = n; s.maxStepName = String(detail).split(':')[1] || ''; }
      }
      s.steps.push({ step, ...(detail ? { detail: String(detail).slice(0, 120) } : {}), atMs: activeMs() });
      send();
    };
    ref.current = s;

    const onTrack = (e) => s.track(e.detail?.step, e.detail?.detail);
    const beat = setInterval(() => { if (document.visibilityState === 'visible') send(); }, HEARTBEAT_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        s.visibleSince = Date.now();
      } else {
        if (s.visibleSince) s.accumulatedMs += Date.now() - s.visibleSince;
        s.visibleSince = null;
        send({ exitType: 'left' });
      }
    };
    const onPageHide = () => { if (!s.exited) { s.exited = true; send({ exitType: 'left' }); } };
    window.addEventListener(EVENT, onTrack);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(beat);
      window.removeEventListener(EVENT, onTrack);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (!s.exited) { s.exited = true; send({ exitType: 'navigated' }); }
      ref.current = null;
    };
  }, []);

  // Stable function the page calls: track('step_shown', '2:location')…
  return useCallback((step, detail) => { try { ref.current?.track(step, detail); } catch { /* ignore */ } }, []);
};

export default useOnboardingAnalytics;
